import { EDTEvent } from '@teetsh/app/src/types/edt';
import { Classroom } from '@teetsh/app/src/types/classroom';
import filterEdtSeancesWithClassrooms from '@teetsh/app/src/components/EDT/filterEdtSeancesWithClassrooms';
import _groupBy from 'lodash-es/groupBy';
import {
  BREAK_EVENT,
  RITUEL_EVENT,
} from '@teetsh/app/src/containers/constants';
import addMinutes from '@teetsh/app/src/utils/time/addMinutes';
import { format } from '@teetsh/app/src/utils/time/format';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import { Tag } from '@teetsh/app/src/types/event';
import _orderBy from 'lodash-es/orderBy';
import { TagHour } from '@teetsh/app/src/components/TagList/EDTEventsTagList';
import { UserLocale } from '@teetsh/app/src/types/user';
import { frenchDatePatternWithSlashesAndHours } from '@teetsh/app/src/utils/time/datePatterns';

interface ClassroomWithTags {
  classroomName: string;
  tags: TagHour[];
}

interface TitleTagClassroom {
  id: string;
  title: string;
  tag: Tag;
  classroomId: string;
  lasting: number;
  start: Date;
}

interface EDTEventWithLasting extends EDTEvent {
  lasting: number;
}

function isArrayStringEqual(a: string[], b: string[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function getVolumeFromEDTEvents(
  events: EDTEvent[],
  classrooms: Classroom[],
  locale: UserLocale,
) {
  const eventsOfClassrooms = filterEdtSeancesWithClassrooms<EDTEvent>(
    events,
    classrooms,
  );
  const eventsByStartEnd = _groupBy(
    eventsOfClassrooms.reduce((acc, num) => {
      if (
        num.type !== BREAK_EVENT &&
        num.type !== RITUEL_EVENT &&
        !num.skipFromVolume
      ) {
        return [...acc, num];
      }
      if (num.type === RITUEL_EVENT) {
        const classroomsTags = classrooms.flatMap(c => c.tags);
        const repartition = (num.repartition || []).reduce((repAcc, rep) => {
          const foundTag = classroomsTags.find(t => t.id === rep.tagId);
          if (!foundTag) {
            return repAcc;
          }
          return [
            ...repAcc,
            {
              ...num,
              tag: foundTag,
              end: addMinutes(num.start, rep.lengthInMinutes),
            },
          ];
        }, [] as EDTEvent[]);
        return [...acc, ...repartition];
      }
      return acc;
    }, [] as EDTEvent[]),
    e =>
      `${format(e.start, locale, frenchDatePatternWithSlashesAndHours)}${format(
        e.end,
        locale,
        frenchDatePatternWithSlashesAndHours,
      )}`,
  );
  let eventsWithCorrectLasting: EDTEventWithLasting[] = [];
  Object.keys(eventsByStartEnd).forEach(date => {
    const value = eventsByStartEnd[date];
    eventsWithCorrectLasting = [
      ...eventsWithCorrectLasting,
      ...value.map(e => ({
        ...e,
        lasting: e.atelier
          ? differenceInMinutes(e.end, e.start) /
            value.filter(v =>
              isArrayStringEqual(v.classroomIds, e.classroomIds),
            ).length
          : differenceInMinutes(e.end, e.start),
      })),
    ];
  });

  const eventsToCount: TitleTagClassroom[] = eventsWithCorrectLasting.reduce(
    (acc, num) => {
      if (!num.tag || !num.tag?.id) {
        return acc;
      }
      return [
        ...acc,
        ...num.classroomIds.map(clId => ({
          id: num.id,
          title: num.title,
          tag: num.tag as Tag,
          classroomId: clId,
          lasting: num.lasting,
          start: num.start,
        })),
      ];
    },
    [] as TitleTagClassroom[],
  );
  const groupedByTagEvents = _groupBy(eventsToCount, e => {
    const { tag } = e;
    return `${tag.title}%%${tag.color}%%${e.classroomId}`;
  });
  let tagHours: TagHour[] = [];
  Object.keys(groupedByTagEvents).forEach(tag => {
    const value = groupedByTagEvents[tag];
    tagHours = [
      ...tagHours,
      {
        tag,
        minutes: value.reduce((acc, num) => {
          const { lasting } = num;
          return acc + lasting;
        }, 0),
        elements: value.map(e => ({
          id: e.id,
          name: e.title,
          start: e.start,
          lasting: e.lasting,
        })),
      },
    ];
  });

  const tagsGroupedByClassroom = _groupBy(tagHours, e => e.tag.split('%%')[2]);

  let result: ClassroomWithTags[] = [];
  Object.keys(tagsGroupedByClassroom).forEach(classroomId => {
    // We only keep classrooms we are interested in
    if (!classrooms.find(cl => cl.id === classroomId)) {
      return;
    }
    const tagsHour = tagsGroupedByClassroom[classroomId];
    const classroomName = (
      classrooms.find(c => c.id === classroomId) || { name: '' }
    ).name;
    result = [
      ...result,
      {
        classroomName,
        tags: _orderBy(tagsHour, r => r.minutes, ['desc']),
      },
    ];
  });
  return result;
}
